import { useLocale, useParamsKey, useResourceList, useResourceShow } from '@koopajs/react';
import { Helmet } from 'react-helmet';
import { Box, Typography, Stack, Button } from '@mui/material';
import { PageContainer } from 'components/temp/PageContainer';
import { ICommittee, IDocumentRecord } from 'types';
import { Breadcrumbs } from 'components/Breadcrumbs';
import { useResourceListQuery } from 'utils/useResourceListQuery';
import { DocumentsTable } from 'components/DocumentRecords/DocumentsTable';
import { useUploadDocumentRecordPermissions } from 'utils/DocumentRecords/useUploadDocumentRecordPermissions';
import { AddDocumentRecordButton } from 'components/DocumentRecords/AddDocumentRecordButton';
import { Loading } from '@koopajs/mui';
import { DelayComponentRender } from 'components/DelayComponentRender';
import { EmptyState } from 'components/EmptyState';
import { Link as RouterLink } from 'react-router-dom';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useFeatureGate } from '@statsig/react-bindings';
import { FeatureLocked } from 'components/FeatureLocked';

export const DocumentRecordsCommittee: React.FC = () => {
  const committeeId = useParamsKey('id');

  const keyPrefix = 'DocumentRecords.CommitteePage';
  const { t } = useLocale();

  const { search, handlePageChange, handleSizeChange } = useResourceListQuery();

  const { committeesUserCanUpload } = useUploadDocumentRecordPermissions();

  const canUserUploadToCommittee = committeesUserCanUpload
    .map((committee) => committee.id)
    .includes(committeeId);

  const {
    resource: committee,
    errorMessage,
    isProcessing: isCommitteeProcessing
  } = useResourceShow<ICommittee>({
    path: '/committees',
    id: committeeId
  });

  const {
    resources: documentRecords,
    state,
    isProcessing,
    totalCount
  } = useResourceList<IDocumentRecord>({
    path: '/document-records',
    searchParams: {
      sort: 'lastDocumentActiveSince:DESC',
      filters: [`committeeId:${committeeId}`],
      size: 10,
      ...search.searchParams
    }
  });

  const { value: isDocumentRecordsEnabled } = useFeatureGate('document-records-preview');

  if (!isDocumentRecordsEnabled) {
    return <FeatureLocked />;
  }

  const extractedErrorMessage = extractErrorMessage(errorMessage);

  if (state === 'loading')
    return (
      <Stack sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <Loading sx={{ backgroundColor: 'transparent', position: 'absolute' }} />
      </Stack>
    );

  if (!committee) {
    if (isCommitteeProcessing) {
      return (
        <Stack sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <Loading sx={{ backgroundColor: 'transparent', position: 'absolute' }} />
        </Stack>
      );
    } else {
      const is404 = errorMessage?.includes('404');
      return (
        <DelayComponentRender
          delay={1250}
          component={
            <EmptyState
              title={is404 ? t(keyPrefix + '.EmptyState.title') : extractedErrorMessage}
              description={is404 && t(keyPrefix + '.EmptyState.description')}
              button={
                <Button component={RouterLink} to="/document-records" variant="contained" disableElevation>
                  {t(keyPrefix + '.EmptyState.goToDocuments')}
                </Button>
              }
            />
          }
        />
      );
    }
  }

  return (
    <>
      <Helmet>
        <title>{`${committee.name} - Panorama`}</title>
      </Helmet>
      <PageContainer sxChildren={{ p: '24px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Breadcrumbs
            hierarchyArray={[{ path: '/document-records', text: t('common:navigation.documents') }]}
            pageTitle={committee.name}
            sx={{ display: { xs: 'none', sm: 'block' } }}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: { sm: 'space-between' },
              alignItems: { xs: 'flex-start' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 3
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                component={RouterLink}
                to="/document-records"
                variant="outlined"
                sx={{ minWidth: 0, px: '5px', display: { sm: 'none' } }}
                aria-label={t('common:labelBack')}
              >
                <ArrowBackIcon sx={{ color: 'primary.main' }} />
              </Button>
              <Typography variant="h1" sx={{ fontSize: '24px' }}>
                {committee.name}
              </Typography>
            </Box>
            {canUserUploadToCommittee ? (
              <AddDocumentRecordButton
                committees={committeesUserCanUpload}
                defaultValues={{ committeeId }}
                isCommitteeFieldDisabled
              />
            ) : (
              // hidden button to reduce layout shift while we wait for permissions request
              <Button sx={{ visibility: 'hidden' }}>.</Button>
            )}
          </Box>

          <DocumentsTable
            documentRecords={documentRecords}
            totalCount={totalCount}
            search={search}
            listState={state}
            onPageChange={handlePageChange}
            onSizeChange={handleSizeChange}
            canUserUploadDocuments={canUserUploadToCommittee}
            committeesUserCanUploadTo={committeesUserCanUpload}
            addDocumentRecordButtonProps={{
              defaultValues: { committeeId }
            }}
            isCommitteeColumnHidden={true}
          />
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            {/* desktop 'back' button */}
            <Button
              component={RouterLink}
              to="/document-records"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              {t('common:labelBack')}
            </Button>
            {/* mobile back arrow button */}
            <Button
              component={RouterLink}
              to="/document-records"
              variant="outlined"
              sx={{ minWidth: 0, px: '5px', display: { sm: 'none' } }}
              aria-label={t('common:labelBack')}
            >
              <ArrowBackIcon sx={{ color: 'primary.main' }} />
            </Button>
          </Box>
        </Box>
      </PageContainer>
    </>
  );
};
